import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from '../contexts/TranslationContext'
import { checkUsernameExists, createUser } from '../services/firebaseDB'
import { ADMIN_ACCESS_ROLES } from '../constants'
import './ParliamentLogin.css'

async function sha256Hex(text) {
  const enc = new TextEncoder()
  const buf = await crypto.subtle.digest('SHA-256', enc.encode(text))
  const bytes = Array.from(new Uint8Array(buf))
  return bytes.map(b => b.toString(16).padStart(2, '0')).join('')
}

function norm(v) {
  return (v || '').trim()
}

export default function ParliamentLogin() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [isLogin, setIsLogin] = useState(true)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const [loginUsername, setLoginUsername] = useState('')
  const [loginPassword, setLoginPassword] = useState('')

  const [registerForm, setRegisterForm] = useState({
    username: '',
    firstName: '',
    lastName: '',
    role: 'student',
    birthday: '',
    password: '',
    confirmPassword: '',
  })

  const from = location.state?.from?.pathname || '/parliament'

  async function handleLogin(e) {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      // Send plaintext credentials to server — server handles hashing and comparison
      const response = await fetch('/api/custom-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: norm(loginUsername),
          password: norm(loginPassword),
        }),
      })

      if (response.status === 429) {
        const data = await response.json().catch(() => ({}))
        setError(
          t('parliamentLogin.tooManyAttempts') ||
          `יותר מדי ניסיונות כניסה. נסה שוב בעוד ${data.retryAfter ? Math.ceil(data.retryAfter / 60) + ' דקות' : 'מעט'}`
        )
        return
      }

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        if (response.status === 401) {
          setError(t('parliamentLogin.invalidCredentials') || 'שם משתמש או סיסמה שגויים')
        } else if (response.status >= 500) {
          setError(t('parliamentLogin.serverError') || 'שגיאת שרת — אנא נסה שוב')
        } else {
          setError(data.error || t('parliamentLogin.loginError') || 'שגיאה בהתחברות')
        }
        return
      }

      const { user } = await response.json()

      if (!user) {
        setError(t('parliamentLogin.loginError') || 'שגיאה בהתחברות')
        return
      }

      localStorage.setItem('session', JSON.stringify(user))

      const canAccessAdmin = Array.isArray(user.roles) &&
        user.roles.some(r => ADMIN_ACCESS_ROLES.includes(String(r).toLowerCase()))

      if (canAccessAdmin) {
        sessionStorage.setItem('adminAuthenticated', 'true')
        sessionStorage.removeItem('justExitedAdminMode')
        window.dispatchEvent(new CustomEvent('adminModeChanged', { detail: { isAdmin: true } }))
        navigate('/admin/dashboard', { replace: true })
        return
      }

      navigate(from, { replace: true })
    } catch (err) {
      console.error('Login error:', err)
      setError(t('parliamentLogin.connectionError') || 'שגיאת חיבור — אנא בדוק את חיבור האינטרנט ונסה שוב')
    } finally {
      setLoading(false)
    }
  }

  async function handleRegister(e) {
    e.preventDefault()
    setError('')

    if (norm(registerForm.password) !== norm(registerForm.confirmPassword)) {
      setError(t('parliamentLogin.passwordMismatch') || 'הסיסמאות לא תואמות')
      return
    }

    if (norm(registerForm.password).length < 4) {
      setError(t('parliamentLogin.passwordTooShort') || 'הסיסמה חייבת להכיל לפחות 4 תווים')
      return
    }

    const allowedRoles = ['student', 'parent']
    if (!allowedRoles.includes(registerForm.role)) {
      setError(t('parliamentLogin.invalidRole') || 'ניתן להירשם רק כתלמיד או הורה')
      return
    }

    // Validate birthday if provided
    if (registerForm.birthday) {
      const bd = new Date(registerForm.birthday)
      const now = new Date()
      if (isNaN(bd.getTime()) || bd > now || bd.getFullYear() < 1900) {
        setError(t('parliamentLogin.invalidBirthday') || 'תאריך לידה לא תקין')
        return
      }
    }

    setLoading(true)

    try {
      const username = norm(registerForm.username)
      const usernameLower = username.toLowerCase()

      let usernameExists
      try {
        usernameExists = await checkUsernameExists(usernameLower)
      } catch (connectionError) {
        console.error('Firestore connection error:', connectionError)
        setError(
          connectionError.message ||
          t('parliamentLogin.connectionError') ||
          'שגיאת חיבור — אנא בדוק את חיבור האינטרנט ונסה שוב'
        )
        return
      }

      if (usernameExists) {
        setError(t('parliamentLogin.usernameExists') || 'שם משתמש כבר קיים')
        return
      }

      // Hash password client-side for storage (server validates same hash on login)
      const passwordHash = await sha256Hex(norm(registerForm.password))

      const userResult = await createUser({
        username,
        usernameLower,
        firstName: norm(registerForm.firstName),
        lastName: norm(registerForm.lastName),
        role: registerForm.role,
        roles: [registerForm.role],
        birthday: registerForm.birthday || '',
        passwordHash,
      })

      const session = {
        uid: userResult.id,
        username,
        usernameLower,
        firstName: registerForm.firstName,
        lastName: registerForm.lastName,
        role: registerForm.role,
        roles: [registerForm.role],
        displayName: `${registerForm.firstName || ''} ${registerForm.lastName || ''}`.trim() || username,
        mode: 'custom-firestore',
      }

      localStorage.setItem('session', JSON.stringify(session))
      navigate(from, { replace: true })
    } catch (err) {
      console.error('Registration error:', err)
      setError(t('parliamentLogin.registerError') || 'שגיאה ברישום')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="parliament-login-page">
      <div className="parliament-login-container">
        <h1 className="parliament-login-title">
          {isLogin
            ? t('parliamentLogin.loginTitle') || 'התחבר לפרלמנט'
            : t('parliamentLogin.registerTitle') || 'הירשם לפרלמנט'}
        </h1>

        <div className="parliament-login-tabs">
          <button
            className={`parliament-tab ${isLogin ? 'active' : ''}`}
            onClick={() => { setIsLogin(true); setError('') }}
          >
            {t('parliamentLogin.login') || 'התחבר'}
          </button>
          <button
            className={`parliament-tab ${!isLogin ? 'active' : ''}`}
            onClick={() => { setIsLogin(false); setError('') }}
          >
            {t('parliamentLogin.register') || 'הירשם'}
          </button>
        </div>

        {error && <div className="parliament-error">{error}</div>}

        {isLogin ? (
          <form onSubmit={handleLogin} className="parliament-login-form">
            <div className="parliament-form-group">
              <label>{t('parliamentLogin.username') || 'שם משתמש'}</label>
              <input
                type="text"
                value={loginUsername}
                onChange={e => setLoginUsername(e.target.value)}
                required
                disabled={loading}
                autoComplete="username"
                maxLength={50}
              />
            </div>
            <div className="parliament-form-group">
              <label>{t('parliamentLogin.password') || 'סיסמה'}</label>
              <input
                type="password"
                value={loginPassword}
                onChange={e => setLoginPassword(e.target.value)}
                required
                disabled={loading}
                autoComplete="current-password"
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary parliament-submit-btn"
              disabled={loading}
            >
              {loading
                ? t('parliamentLogin.loggingIn') || 'מתחבר...'
                : t('parliamentLogin.login') || 'התחבר'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleRegister} className="parliament-login-form">
            <div className="parliament-form-row">
              <div className="parliament-form-group">
                <label>{t('parliamentLogin.username') || 'שם משתמש'}</label>
                <input
                  type="text"
                  value={registerForm.username}
                  onChange={e => setRegisterForm({ ...registerForm, username: e.target.value })}
                  required
                  disabled={loading}
                  autoComplete="username"
                  maxLength={50}
                />
              </div>
              <div className="parliament-form-group">
                <label>{t('parliamentLogin.firstName') || 'שם פרטי'}</label>
                <input
                  type="text"
                  value={registerForm.firstName}
                  onChange={e => setRegisterForm({ ...registerForm, firstName: e.target.value })}
                  required
                  disabled={loading}
                  autoComplete="given-name"
                />
              </div>
            </div>
            <div className="parliament-form-row">
              <div className="parliament-form-group">
                <label>{t('parliamentLogin.lastName') || 'שם משפחה'}</label>
                <input
                  type="text"
                  value={registerForm.lastName}
                  onChange={e => setRegisterForm({ ...registerForm, lastName: e.target.value })}
                  required
                  disabled={loading}
                  autoComplete="family-name"
                />
              </div>
              <div className="parliament-form-group">
                <label>{t('parliamentLogin.role') || 'תפקיד'}</label>
                <select
                  value={registerForm.role}
                  onChange={e => setRegisterForm({ ...registerForm, role: e.target.value })}
                  required
                  disabled={loading}
                >
                  <option value="student">{t('users.role.student') || 'תלמיד'}</option>
                  <option value="parent">{t('users.role.parent') || 'הורה'}</option>
                </select>
              </div>
            </div>
            <div className="parliament-form-group">
              <label>{t('parliamentLogin.birthday') || 'תאריך לידה'}</label>
              <input
                type="date"
                value={registerForm.birthday}
                onChange={e => setRegisterForm({ ...registerForm, birthday: e.target.value })}
                disabled={loading}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
            <div className="parliament-form-row">
              <div className="parliament-form-group">
                <label>{t('parliamentLogin.password') || 'סיסמה'}</label>
                <input
                  type="password"
                  value={registerForm.password}
                  onChange={e => setRegisterForm({ ...registerForm, password: e.target.value })}
                  required
                  disabled={loading}
                  autoComplete="new-password"
                  minLength={4}
                  maxLength={100}
                />
              </div>
              <div className="parliament-form-group">
                <label>{t('parliamentLogin.confirmPassword') || 'אישור סיסמה'}</label>
                <input
                  type="password"
                  value={registerForm.confirmPassword}
                  onChange={e => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                  required
                  disabled={loading}
                  autoComplete="new-password"
                  minLength={4}
                  maxLength={100}
                />
              </div>
            </div>
            <button
              type="submit"
              className="btn btn-primary parliament-submit-btn"
              disabled={loading}
            >
              {loading
                ? t('parliamentLogin.registering') || 'נרשם...'
                : t('parliamentLogin.register') || 'הירשם'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
